import hash from "@emotion/hash"
import styled from "@emotion/styled"
import { useLink } from "@react-aria/link"
import * as uniform from "@uniformdev/context-react"
import CategoryLink from "components/footer/CategoryLink"
import { useNavigation } from "contexts/Navigation"
import { bold } from "css/text"
import { useId, useRef } from "react"

const cssPrimary = `css-${hash("is_primary")}`
const cssStartsNewGroup = `css-${hash("starts_new_group")}`

const Root = styled.li`
  > a,
  > span {
    ${bold}
    display: block;
    font-size: var(--baseline-font-size); // 14px > 16px

    &.${cssPrimary} {
      font-size: clamp(1.125rem, 0.875rem + 0.625vw, 1.625rem); // 18px > 26px
    }
  }

  > a {
    color: inherit;
    text-decoration: none;
    transition: color 0.3s;
    display: block;

    @media (hover: hover) {
      :hover {
        color: rgb(var(--green));
      }
    }
  }

  li {
    font-size: var(--baseline-font-size); // 14px > 16px
    font-weight: normal;

    &.${cssStartsNewGroup} {
      margin-top: var(--baseline);
    }

    a,
    button {
      all: unset;

      align-items: baseline; // center increased height, broke baseline grid
      color: rgb(var(--light-black));
      cursor: pointer;
      display: inline-flex;
      gap: 0.5rem;
      text-decoration: none;
      transition: color 0.3s;

      svg {
        fill: currentColor;
        height: 0.875rem;
        width: 0.875rem;
        transform: translateY(0.2ex); // tweak alignment because of align-items: baseline on parent
      }

      @media (hover: hover) {
        :hover {
          color: rgb(var(--green));
        }
      }

      &.reverseIcon {
        flex-direction: row-reverse;
      }

      &.inline {
        display: inline;

        svg {
          padding-inline-start: 0.5rem;
          height: 0.75rem;
          width: 0.75rem;
        }
      }
    }
  }
`

function isLinkVisible(rules, scores) {
  for (const rule of rules || []) {
    if (!rule.criteria?.name?.crit) break

    const hit = rule.criteria.name.crit.every(({ l: signal, op: operator, r: value }) => operator === "=" && +scores[signal] === +value)

    if (rule.type === "include" && !hit) return false
    if (rule.type === "exclude" && hit) return false
  }

  return true
}

export default function Category({ label, href, is_primary, links, social, accessibility, wechat, modal }) {
  const scores = uniform.useScores()
  const rlink = useRef()
  const { linkProps } = useLink({}, rlink)
  const navigation = useNavigation()
  const uid = useId()

  const navigate = e => {
    e.preventDefault()
    navigation.navigate(e.currentTarget.getAttribute("href"))
  }

  return (
    <Root>
      {href ? (
        <a id={uid} {...linkProps} ref={rlink} href={navigation.localize(href)} onClick={navigate} className={is_primary ? cssPrimary : null}>
          {label}
        </a>
      ) : (
        <span id={uid} className={is_primary ? cssPrimary : null}>
          {label}
        </span>
      )}
      <ul aria-labelledby={uid}>
        {links?.filter(link => isLinkVisible(link.p13n, scores)).map(link => (
          <li key={link.label} className={link.starts_new_group ? cssStartsNewGroup : null}>
            <CategoryLink {...link} social={social} opensModal={accessibility} wechat={wechat} modal={modal} />
          </li>
        ))}
      </ul>
    </Root>
  )
}
